import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { FormPanelProps, EmailChangeModalFormVO } from './type'
import type { SubmitHandler } from 'react-hook-form'

import { Button, FormFieldText, ErrorMessageList } from '@/components/ui'
import { OPERATIONAL_ERROR_MESSAGE } from '@/const'
import { useChangeEmail } from '@/hooks/features/auth'
import { getErrorMessages } from '@/utils/text'

import { schema, formLabels } from './const'

export const FormPanel = ({
  defaultValues,
  onSubmit,
  onClose,
}: FormPanelProps) => {
  const { t } = useTranslation()
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const { mutate, isLoading } = useChangeEmail({})

  const { register, formState, handleSubmit, reset } =
    useForm<EmailChangeModalFormVO>({
      resolver: schema && zodResolver(schema),
      defaultValues,
    })

  const submitHandler: SubmitHandler<EmailChangeModalFormVO> = useCallback(
    ({ newEmail }) => {
      if (errorMessages) {
        setErrorMessages([])
      }

      mutate(
        { data: { newEmail } },
        {
          onSuccess: () => {
            onSubmit()
            reset()
          },
          onError: (error) => {
            if (error.response?.status === 400) {
              setErrorMessages(
                getErrorMessages({
                  fields: error.response.data.fields,
                  fieldNames: formLabels,
                  errorMessages: OPERATIONAL_ERROR_MESSAGE.USER.CHANGE_EMAIL,
                })
              )
            }
          },
        }
      )
    },
    [errorMessages, mutate, onSubmit, reset]
  )

  return (
    <div className="tw-w-full tw-px-2 tw-pb-2">
      {errorMessages.length > 0 && (
        <div className={clsx('tw-mt-4')}>
          <ErrorMessageList errorMessages={errorMessages} />
        </div>
      )}

      <form
        onSubmit={handleSubmit(submitHandler)}
        id="email-change-form"
        className={clsx('tw-mt-20')}
      >
        <FormFieldText
          label={t('auth.email.newEmail')}
          required
          placeholder="kikuya@example.com"
          error={formState.errors['newEmail']}
          registration={register('newEmail')}
        />

        <div className={clsx('tw-mt-12 tw-flex tw-items-center tw-space-x-3')}>
          <Button
            type="button"
            variant="normal"
            disabled={isLoading}
            onClick={onClose}
          >
            {t('auth.email.close')}
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {t('auth.email.submit')}
          </Button>
        </div>
      </form>
    </div>
  )
}
